import React, { useEffect, useState } from 'react';

interface BasemapSwitchControlProps {
  onClick: () => void;
}

const HINT_STORAGE_KEY = 'sfi.basemapSwitchHinted';

const readHinted = (): boolean => {
  try {
    return localStorage.getItem(HINT_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
};

const writeHinted = () => {
  try {
    localStorage.setItem(HINT_STORAGE_KEY, '1');
  } catch {
    // ignore quota / privacy-mode failures
  }
};

/**
 * Opens the basemap picker. Designed to sit as the leftmost child of
 * `<MapZoomControl>` — the parent already handles Leaflet click/scroll
 * propagation for the whole control stack.
 *
 * Renders a one-time "Try other basemaps" hint chip below the button on
 * first visit. The hint dismisses on first click of the button (the user
 * has discovered the affordance) or on explicit dismissal via its ×
 * button. Persists in localStorage so it doesn't reappear next session.
 */
export const BasemapSwitchControl: React.FC<BasemapSwitchControlProps> = ({ onClick }) => {
  // Default to "hinted" so SSR / initial render doesn't flash the chip
  // before localStorage is read; the effect below flips it on if the
  // user hasn't seen the hint yet.
  const [hinted, setHinted] = useState(true);

  useEffect(() => {
    setHinted(readHinted());
  }, []);

  const dismissHint = () => {
    if (!hinted) {
      writeHinted();
      setHinted(true);
    }
  };

  const handleClick = () => {
    dismissHint();
    onClick();
  };

  return (
    <div className="relative h-full">
      <button
        onClick={handleClick}
        title="Change basemap"
        aria-label="Change basemap"
        className="h-full px-3 bg-figma-card rounded-l-[10px] border-r border-figma-panel/50 flex items-center justify-center hover:bg-figma-panel transition-smooth"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6.5" stroke="#c1f04f" strokeWidth="1.3" />
          <ellipse cx="8" cy="8" rx="2.5" ry="6.5" stroke="#c1f04f" strokeWidth="1.3" />
          <line x1="1.5" y1="8" x2="14.5" y2="8" stroke="#c1f04f" strokeWidth="1.3" />
        </svg>
      </button>

      {!hinted && (
        <div
          role="status"
          aria-live="polite"
          className="absolute top-full mt-2 right-0 z-[1100] flex items-center gap-2 pl-3 pr-2 py-2 rounded-figma-button bg-figma-card border border-figma-mint/60 shadow-figma-card whitespace-nowrap pointer-events-auto"
        >
          {/* Up arrow pointing at the basemap button */}
          <span
            aria-hidden="true"
            className="absolute -top-1.5 right-3 w-3 h-3 rotate-45 bg-figma-card border-l border-t border-figma-mint/60"
          />
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" className="flex-shrink-0">
            <path d="M6 10V2M2 5l4-3 4 3" stroke="#46ce85" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-[12px] font-roboto text-figma-text">Try other basemaps</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              dismissHint();
            }}
            aria-label="Dismiss basemap hint"
            className="ml-1 w-5 h-5 flex items-center justify-center rounded-full text-figma-ink-mute hover:text-figma-text hover:bg-figma-panel/60 transition-smooth"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
